/**
 * Paleta oficial de disciplinas — M2.16.
 *
 * Standalone (sin depender de `zoneColors.ts` que es para zonas de
 * intensidad). Convención visual alineada con Strava/TrainingPeaks: swim
 * azul, run naranja, bici ámbar (no verde — colisionaría con strength),
 * strength verde, brick púrpura, other gris.
 *
 * Cada entrada expone:
 *  - `hex`: para SVG (`<rect fill={...}/>`) — los segmentos SVG no
 *    soportan `class="bg-*"`.
 *  - `tw`: token Tailwind (sin el prefijo `bg-/text-/border-`) para que el
 *    consumidor lo componga (`bg-${tw}`, `text-${tw}`).
 *  - `label`: nombre en español para leyendas, tooltips, aria-labels.
 */
export type DisciplineKey =
  | 'swim'
  | 'bike'
  | 'run'
  | 'strength'
  | 'brick'
  | 'other';

export interface DisciplineColor {
  hex: string;
  tw: string;
  label: string;
}

export const DISCIPLINE_COLORS: Record<DisciplineKey, DisciplineColor> = {
  swim:     { hex: '#0ea5e9', tw: 'sky-500',     label: 'Natación' },
  bike:     { hex: '#f59e0b', tw: 'amber-500',   label: 'Bici' },
  run:      { hex: '#f97316', tw: 'orange-500',  label: 'Carrera' },
  strength: { hex: '#10b981', tw: 'emerald-500', label: 'Fuerza' },
  brick:    { hex: '#a855f7', tw: 'purple-500',  label: 'Brick' },
  other:    { hex: '#64748b', tw: 'slate-500',   label: 'Otros' },
};

/**
 * Orden canónico para iterar disciplinas (stack en gráficos apilados,
 * orden en leyendas, etc.). Mantenerlo estable entre componentes evita
 * inconsistencias visuales.
 */
export const DISCIPLINE_ORDER: readonly DisciplineKey[] = [
  'swim',
  'bike',
  'run',
  'strength',
  'brick',
  'other',
] as const;

/**
 * Devuelve la entrada de paleta para una disciplina dada. Si llega una
 * cadena no reconocida (defensa contra payload con disciplinas legacy),
 * cae a `other` para no romper el render.
 */
export function disciplineColor(discipline: string): DisciplineColor {
  if (discipline in DISCIPLINE_COLORS) {
    return DISCIPLINE_COLORS[discipline as DisciplineKey];
  }
  return DISCIPLINE_COLORS.other;
}
